import { Menu, LogOut, User, ChevronDown } from 'lucide-react'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getInitials } from '@/lib/utils'

interface HeaderProps {
  title: string
  subtitle?: string
  onLogout?: () => void
}

export function Header({ title, subtitle, onLogout }: HeaderProps) {
  const { toggleSidebar } = useUIStore()
  const { user, isAdmin } = useAuthStore()

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-white px-4 sm:px-6">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={toggleSidebar}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle menu</span>
      </Button>

      {/* Page title */}
      <div className="flex-1">
        <h1 className="text-lg font-semibold text-gray-900 sm:text-xl">
          {title}
        </h1>
        {subtitle && (
          <p className="text-xs text-gray-500 sm:text-sm">{subtitle}</p>
        )}
      </div>

      {/* Status indicator */}
      <div className="hidden items-center gap-2 sm:flex">
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span className="text-xs text-gray-500">System Online</span>
        </div>
      </div>

      {/* User menu - always show if onLogout is provided */}
      {(user || onLogout) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary-100 text-primary-700 text-xs">
                  {user ? getInitials(user.name || user.email) : '?'}
                </AvatarFallback>
              </Avatar>
              {user && (
                <div className="hidden flex-col items-start text-left sm:flex">
                  <span className="text-sm font-medium">
                    {user.name || 'User'}
                  </span>
                  <span className="text-xs text-gray-500">{user.email}</span>
                </div>
              )}
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {user && (
              <>
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user.name || 'User'}</span>
                    <span className="text-xs font-normal text-gray-500">
                      {user.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem className="text-purple-600">
                    <span className="mr-2 text-xs">Admin</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
              </>
            )}
            {onLogout && (
              <DropdownMenuItem
                className="text-red-600"
                onClick={onLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  )
}
